import { useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  Route,
  Routes,
  useNavigate,
  useLocation,
} from "react-router-dom-v5-compat";
import { trackEvent } from "@hopper-b2b/api";
import { logger } from "@hopper-b2b/datadog";
import { useI18nContext } from "@hopper-b2b/i18n";
import { B2B_PORTAL_AUTH_REDIRECT_TO } from "@hopper-b2b/types";
import { B2BLoadingPopup } from "@hopper-b2b/ui";
import queryStringParser from "query-string";

import { impersonateUserSession } from "../../api/session/impersonateUserSession";
import { startSession } from "../../api/session/startSession";
import { UserContext } from "../../App";
import { useFlightActionEntryRedirect } from "../../hooks/useFlightActionEntryRedirect";
import { removeLocalStorageTrackingEvents } from "../../hooks/useFlightLocalStorageTracking";
import { COLOR_MODE_COOKIE_NAME } from "../../utils/colors";
import { closeNubankWebview } from "../../utils/nubankHandoff";
import { isCommerceCheckoutPath } from "../../utils/nupayCommerceCheckoutPath";
import { resolveMisroutedLegacyNupayOAuthRedirect } from "../../utils/nupayOAuthCallbackRedirect";
import {
  type FlightActionEntry,
  getLogSafeRedirectMetadata,
  isFlightActionEntryShapedPath,
  isSafePostLoginRedirectPath,
  parseFlightActionEntryPath,
  toLogSafeRedirectPath,
} from "../../utils/postLoginRedirect";
import { setNubankDatadogUser } from "../../utils/setNubankDatadogUser";
import {
  PATH_AUTH_INVALID_SESSION,
  PATH_AUTH_START_SESSION,
  PATH_HOME,
  PATH_TRIPS,
  ROUTE_AUTH_INVALID_SESSION,
  ROUTE_AUTH_LOGOUT,
} from "../../utils/urlPaths";
import ExpiredSession from "../ExpiredSession/component";
import {
  COLOR_MODE_PARAM,
  IMPERSONATE_COOKIE_NAME,
  IMPERSONATE_USER_PARAM,
  LOGOUT_REDIRECT,
  REDIRECT_URI_PARAM,
  SESSION_TOKEN_PARAM,
} from "./constants";
import "./styles.scss";

export const StartSessionComponent = () => {
  const { t } = useI18nContext();

  const navigate = useNavigate();
  const location = useLocation();
  const { sessionInfo, updateSessionInfo, setError } = useContext(UserContext);

  // A flight action entry (`redirect_uri=/trips/actions/…`) is not
  // navigated to directly. It parks here after the session is
  // established, the component keeps rendering its loading state, and
  // useFlightActionEntryRedirect completes the navigation, commerce
  // or legacy b2b, once the order's capability decision is final.
  const [pendingFlightAction, setPendingFlightAction] =
    useState<FlightActionEntry | null>(null);
  useFlightActionEntryRedirect(pendingFlightAction);

  // NU-18 diagnostic: log every time StartSessionComponent mounts
  useEffect(() => {
    logger().info("NU-18 diag: StartSessionComponent mounted", {
      hasExistingSession: !!sessionInfo?.hopperSessionToken,
      currentPath: window.location.pathname,
      hasQueryParams: !!location?.search,
      queryParamKeys: Array.from(
        new URLSearchParams(location?.search).keys()
      ).join(","),
      visibilityState: document.visibilityState,
    });
  }, [sessionInfo, location?.search]);

  const authGenericCodeFlow = useCallback(async () => {
    const redirectToSessionStorageUrl = (url: string) => {
      navigate(url, { replace: true });
      sessionStorage.removeItem(B2B_PORTAL_AUTH_REDIRECT_TO);
    };
    // Using replace: true to remove the auth page from browser history.
    // This prevents users from navigating back to the auth page and resubmitting
    // an already-used auth code, which would cause a "grant already claimed" error.
    const handleRedirect = (redirectUrlFromStartSession: string | null) => {
      const redirectTo = sessionStorage.getItem(B2B_PORTAL_AUTH_REDIRECT_TO);
      logger().info("Post-login redirect received", {
        event: "post_login_redirect_received",
        ...getLogSafeRedirectMetadata(redirectUrlFromStartSession),
      });

      if (redirectUrlFromStartSession) {
        // NU-470: `redirect_uri` is caller-controlled input, never
        // navigate to it unvalidated. Off-origin, protocol-relative,
        // and traversal shapes land on home instead (no open redirect,
        // no crash). Logs carry only the path portion (SEC-001/SEC-002
        // precedent: redirect query strings can hold codes/state).
        if (!isSafePostLoginRedirectPath(redirectUrlFromStartSession)) {
          logger().warn("NU-470: rejected unsafe redirect_uri", {
            redirectPath: toLogSafeRedirectPath(redirectUrlFromStartSession),
          });
          navigate(PATH_HOME, { replace: true });
          return;
        }

        const flightAction = parseFlightActionEntryPath(
          redirectUrlFromStartSession
        );
        if (flightAction) {
          setPendingFlightAction(flightAction);
          return;
        }
        // An action-shaped path that failed to parse (bad action or
        // orderId shape, case variants) is a malformed deep link, not
        // a real page, so fail safely to the trips list rather than a
        // dead route inside the /trips/* wildcard.
        if (isFlightActionEntryShapedPath(redirectUrlFromStartSession)) {
          logger().warn("NU-470: malformed flight action entry path", {
            redirectPath: toLogSafeRedirectPath(redirectUrlFromStartSession),
          });
          navigate(PATH_TRIPS, { replace: true });
          return;
        }

        // NU-474: commerce-portals paths (/stays/*, /air/*) are served
        // by a separate app behind the same-origin Istio gateway. SPA
        // navigate() cannot reach them (the catch-all route redirects
        // to home), so use a full-page redirect instead. The safety
        // check at the top of handleRedirect already validated the path.
        if (isCommerceCheckoutPath(redirectUrlFromStartSession)) {
          window.location.replace(redirectUrlFromStartSession);
          return;
        }

        navigate(redirectUrlFromStartSession, { replace: true });
      } else if (redirectTo) {
        redirectToSessionStorageUrl(redirectTo);
      } else navigate(PATH_HOME, { replace: true });
    };

    // Extract params from URL
    const searchParams = new URLSearchParams(location?.search);
    const authToken = searchParams.get(SESSION_TOKEN_PARAM);
    const impersonateUserId = searchParams.get(IMPERSONATE_USER_PARAM);
    const redirectUri = searchParams.get(REDIRECT_URI_PARAM);
    const colorMode = searchParams.get(COLOR_MODE_PARAM);

    // NU-18 fix: strip the OAuth code (SESSION_TOKEN_PARAM = "code") from the
    // native browser URL so that if iOS kills and restores the WebView, it
    // won't replay a consumed authorization code.
    // Only removes the `code` param to preserve any other query params.
    // Declared outside try/catch so it's accessible from the catch block.
    const stripCodeFromUrl = () => {
      const url = new URL(window.location.href);
      url.searchParams.delete(SESSION_TOKEN_PARAM);
      window.history.replaceState(null, "", url.pathname + url.search);
    };

    try {
      // Handle color mode
      if (colorMode) {
        document.cookie = `${COLOR_MODE_COOKIE_NAME}=${colorMode};path=/`;
      }

      // Impersonation flow
      if (impersonateUserId) {
        // NU-18: Strip code before the async call (same rationale as the
        // normal auth flow — prevents iOS WebView kill/restore replay).
        stripCodeFromUrl();
        const newSessionInfo = await impersonateUserSession(impersonateUserId);
        document.cookie = `${IMPERSONATE_COOKIE_NAME}=true;path=/`;
        updateSessionInfo(newSessionInfo);
        if (newSessionInfo?.hopperUserId) {
          setNubankDatadogUser({ id: newSessionInfo.hopperUserId });
        }

        handleRedirect(redirectUri);
        return;
      }

      // Normal auth flow
      if (authToken) {
        const misroutedLegacyNupayRedirect =
          resolveMisroutedLegacyNupayOAuthRedirect(location?.search ?? "");
        if (misroutedLegacyNupayRedirect) {
          window.location.replace(misroutedLegacyNupayRedirect);
          return;
        }

        logger().info("NU-18 diag: startSession called", {
          hasAuthToken: !!authToken,
          hasExistingSession: !!sessionInfo?.hopperSessionToken,
        });
        document.cookie = `${IMPERSONATE_COOKIE_NAME}=false;path=/`;
        // NU-18: Strip the OAuth code from the URL BEFORE the async call.
        // `authToken` is already captured in a local variable, so the
        // startSession call is unaffected.  Moving this ahead of the await
        // closes a race window where iOS could kill the WebView while
        // startSession is in-flight, causing a code-replay on restore.
        stripCodeFromUrl();
        const newSessionInfo = await startSession(authToken);
        updateSessionInfo(newSessionInfo);
        // Attach Hopper UserId to RUM + Logs before price-watch redirect diagnostics.
        if (newSessionInfo?.hopperUserId) {
          setNubankDatadogUser({ id: newSessionInfo.hopperUserId });
        }
        handleRedirect(redirectUri);
        return;
      }

      // No code, no session → close webview (user must restart from Nubank app)
      logger().warn("No auth code and no session, closing webview");
      closeNubankWebview();
    } catch (err: any) {
      logger().error("NU-18 diag: startSession FAILED", {
        errorStatus: err?.response?.status,
        hasExistingSession: !!sessionInfo?.hopperSessionToken,
      });
      console.error("Error starting session", err);

      // Strip the code on failure too, to prevent retry loops
      // if iOS kills and restores the WebView after a failed startSession.
      stripCodeFromUrl();

      setError(err?.response?.data);
      navigate(PATH_AUTH_INVALID_SESSION, { replace: true });
    }
  }, [navigate, location?.search, updateSessionInfo, setError]);

  useEffect(() => {
    const queryString = queryStringParser.parse(location.search);

    if (queryString) authGenericCodeFlow();
    else navigate(PATH_AUTH_INVALID_SESSION);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <B2BLoadingPopup
      open
      message={t("loading")}
      popupSize={"mobile"}
      fullScreen
      className={"nubank-auth-start-container"}
    />
  );
};

export const InvalidSessionComponent = () => {
  const { error } = useContext(UserContext);
  const hasTrackedSessionExpired = useRef(false);

  useEffect(() => {
    if (!hasTrackedSessionExpired.current) {
      hasTrackedSessionExpired.current = true;
      void trackEvent({
        eventName: "session_expired_viewed",
        properties: {
          trigger: error ? "start_session_failure" : "unknown",
        },
      });
    }

    // key is from loding-checkout initalContext
    removeLocalStorageTrackingEvents("nubank-tracking-properties");
    // key is from flight-checkout initalContext
    removeLocalStorageTrackingEvents("nubank-tracking-properties-air");
  }, [error]);
  return <ExpiredSession />;
};

export const LogoutComponent = () => {
  const navigate = useNavigate();
  const { t } = useI18nContext();
  const logout = async () => {
    try {
      // TODO: add logout api call
      //   const logoutResponse = await postLogout();
      //   if (logoutResponse.destinationUrl)
      //   window.location.replace(logoutResponse.destinationUrl);
      // history.push(LOGOUT_REDIRECT);
      navigate(LOGOUT_REDIRECT);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    logout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <B2BLoadingPopup
      open
      message={t("loggingOut")}
      popupSize={"mobile"}
      fullScreen
    />
  );
};

export const AuthModule = () => {
  return (
    <Routes>
      <Route path={ROUTE_AUTH_LOGOUT} element={<LogoutComponent />} />
      <Route
        path={PATH_AUTH_START_SESSION}
        element={<StartSessionComponent />}
      />
      <Route
        path={ROUTE_AUTH_INVALID_SESSION}
        element={<InvalidSessionComponent />}
      />
    </Routes>
  );
};
